import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import { formatDate } from '@/utils';
import { Button, Card, CardHeader, Badge, ContentLoader } from '@/components/common';
import { usePolicyStatus } from '@/hooks/useIntegrations';
import { getErrorMessage } from '@/services/api';

type StepStatus = 'complete' | 'current' | 'upcoming' | 'failed';

interface StatusStep {
  id: string;
  title: string;
  description: string;
  status: StepStatus;
}

function getStatusBadgeVariant(status: string): 'success' | 'warning' | 'error' | 'default' | 'info' {
  switch (status) {
    case 'verified':
    case 'completed':
    case 'EFFECTIVE':
    case 'passed':
      return 'success';
    case 'pending':
    case 'in_progress':
    case 'processing':
      return 'warning';
    case 'failed':
    case 'rejected':
    case 'mismatch':
      return 'error';
    case 'not_required':
      return 'info';
    default:
      return 'default';
  }
}

function buildStatusSteps(policyData: {
  policy_status?: string;
  kyc_status?: string;
  payment_status?: string;
  break_in_status?: string;
}): StatusStep[] {
  const steps: StatusStep[] = [];
  const status = policyData.policy_status || 'pending';
  const isBreakIn = !!policyData.break_in_status;

  // Step 1: Proposal Created
  steps.push({
    id: 'proposal',
    title: 'Proposal Created',
    description: 'Your proposal has been submitted',
    status: 'complete',
  });

  // Step 2: KYC Verification
  const kycDone = policyData.kyc_status === 'verified';
  const kycFailed = policyData.kyc_status === 'failed' || policyData.kyc_status === 'mismatch';
  steps.push({
    id: 'kyc',
    title: 'KYC Verification',
    description: kycFailed ? 'KYC verification failed' : kycDone ? 'KYC verified' : 'Awaiting KYC verification',
    status: kycFailed ? 'failed' : kycDone ? 'complete' : status === 'pending' ? 'current' : 'upcoming',
  });

  // Break-in: Inspection step
  if (isBreakIn) {
    const inspDone = policyData.break_in_status === 'passed';
    const inspFailed = policyData.break_in_status === 'failed';
    steps.push({
      id: 'inspection',
      title: 'Vehicle Inspection',
      description: inspFailed ? 'Inspection failed' : inspDone ? 'Inspection completed' : 'Awaiting vehicle inspection',
      status: inspFailed ? 'failed' : inspDone ? 'complete' : kycDone ? 'current' : 'upcoming',
    });
  }

  // Step 3: Payment
  const paymentDone = policyData.payment_status === 'completed';
  const paymentFailed = policyData.payment_status === 'failed';
  const prevDone = isBreakIn
    ? policyData.break_in_status === 'passed'
    : kycDone;
  steps.push({
    id: 'payment',
    title: 'Payment',
    description: paymentFailed ? 'Payment failed' : paymentDone ? 'Payment received' : 'Awaiting payment',
    status: paymentFailed ? 'failed' : paymentDone ? 'complete' : prevDone ? 'current' : 'upcoming',
  });

  // Step 4: Policy Issued
  const policyIssued = status === 'EFFECTIVE';
  steps.push({
    id: 'issued',
    title: 'Policy Issued',
    description: policyIssued ? 'Policy is active' : 'Awaiting policy issuance',
    status: policyIssued ? 'complete' : paymentDone ? 'current' : 'upcoming',
  });

  return steps;
}

export function PolicyStatusPage() {
  const { policyNumber } = useParams<{ policyNumber: string }>();

  const { data: policyData, isLoading, error, dataUpdatedAt } = usePolicyStatus(policyNumber!);

  if (isLoading) {
    return <ContentLoader />;
  }

  if (error || !policyData) {
    /*
     * BOARD #245 part 2 -- honest blocked states.
     *
     * This used to say "Policy not found / Unable to load policy status
     * information" for every failure, which was wrong most of the time. This
     * endpoint asks the INSURER, so the common answers are
     * `404 No integration configured for X in <environment>` and
     * `502 <whatever the insurer said>` — a policy that exists perfectly well
     * on this platform, about which the insurer could not be reached. Telling
     * the broker their policy does not exist sends them looking for the wrong
     * problem.
     *
     * The server's own sentence is rendered verbatim; nothing is re-worded here.
     */
    const detail = error ? getErrorMessage(error) : '';
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          The insurer could not be asked about this policy
        </h2>
        <p className="text-gray-500 dark:text-gray-400 mt-2 max-w-xl mx-auto">
          {detail ||
            'The status request returned nothing. This screen reports the insurer\u2019s answer and has no record of its own.'}
        </p>
        <p className="text-xs text-gray-400 mt-3 max-w-xl mx-auto">
          This says nothing about whether the policy exists here — it is about reaching the insurer.
          Open the policy itself to see what this platform has recorded.
        </p>
        <div className="mt-4 flex items-center justify-center gap-3">
          <Link to={`/policies?search=${encodeURIComponent(policyNumber ?? '')}`}>
            <Button variant="primary">Find this policy</Button>
          </Link>
          <Link to="/quotes">
            <Button variant="outline" leftIcon={<ArrowLeftIcon className="w-5 h-5" />}>
              Back to Quotes
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const statusSteps = buildStatusSteps(policyData);
  const isEffective = policyData.policy_status === 'EFFECTIVE';
  const lastUpdated = dataUpdatedAt ? formatDate(dataUpdatedAt, 'HH:mm:ss') : '';

  return (
    <div>
      <PageHeader
        title="Policy Status"
        subtitle={`Policy: ${policyNumber}`}
        breadcrumbs={[
          { label: 'Quotes', href: '/quotes' },
          { label: 'Status' },
        ]}
        actions={
          <Link to="/quotes">
            <Button variant="outline" leftIcon={<ArrowLeftIcon className="w-5 h-5" />}>
              Back to Quotes
            </Button>
          </Link>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Status Timeline */}
          <Card>
            <CardHeader
              title="Status Timeline"
              action={
                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <ArrowPathIcon className="w-3.5 h-3.5" />
                  <span>Auto-refreshes every 30s</span>
                  {lastUpdated && <span>| Last: {lastUpdated}</span>}
                </div>
              }
            />
            <div className="space-y-0">
              {statusSteps.map((step, index) => {
                const isLast = index === statusSteps.length - 1;
                return (
                  <div key={step.id} className="relative flex gap-4">
                    {/* Connector line */}
                    {!isLast && (
                      <div
                        className={`absolute left-4 top-10 w-0.5 h-full -ml-px ${
                          step.status === 'complete'
                            ? 'bg-green-400'
                            : step.status === 'failed'
                            ? 'bg-red-300'
                            : 'bg-gray-200 dark:bg-gray-700'
                        }`}
                      />
                    )}

                    {/* Step indicator */}
                    <div className="relative flex-shrink-0">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          step.status === 'complete'
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                            : step.status === 'current'
                            ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400 ring-4 ring-yellow-50 dark:ring-yellow-800'
                            : step.status === 'failed'
                            ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-400'
                        }`}
                      >
                        {step.status === 'complete' && <CheckCircleIcon className="w-4 h-4" />}
                        {step.status === 'current' && <ClockIcon className="w-4 h-4" />}
                        {step.status === 'failed' && <XCircleIcon className="w-4 h-4" />}
                        {step.status === 'upcoming' && <span className="text-xs font-medium">{index + 1}</span>}
                      </div>
                    </div>

                    {/* Step content */}
                    <div className="pb-8">
                      <p
                        className={`text-sm font-medium ${
                          step.status === 'complete'
                            ? 'text-gray-900 dark:text-white'
                            : step.status === 'current'
                            ? 'text-yellow-700 dark:text-yellow-300'
                            : step.status === 'failed'
                            ? 'text-red-700 dark:text-red-300'
                            : 'text-gray-500 dark:text-gray-400'
                        }`}
                      >
                        {step.title}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{step.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* KYC Section */}
          {policyData.kyc_status && (
            <Card>
              <CardHeader title="KYC Verification" />
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">KYC Status</span>
                  <Badge variant={getStatusBadgeVariant(policyData.kyc_status)}>
                    {policyData.kyc_status}
                  </Badge>
                </div>
                {policyData.kyc_mismatch_type && (
                  <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                    <div className="flex items-start gap-2">
                      <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">KYC Mismatch</p>
                        <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">{policyData.kyc_mismatch_type}</p>
                      </div>
                    </div>
                  </div>
                )}
                {policyData.kyc_upload_link && (
                  <a
                    href={policyData.kyc_upload_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block"
                  >
                    <Button variant="outline" size="sm">
                      Upload KYC Documents
                    </Button>
                  </a>
                )}
              </div>
            </Card>
          )}

          {/* Break-in Inspection Section */}
          {policyData.break_in_status && (
            <Card>
              <CardHeader title="Break-in Inspection" />
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Inspection Status</span>
                  <Badge variant={getStatusBadgeVariant(policyData.break_in_status)}>
                    {policyData.break_in_status}
                  </Badge>
                </div>
              </div>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status Card */}
          <Card>
            <CardHeader title="Policy Status" />
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Status</span>
                <Badge variant={getStatusBadgeVariant(policyData.policy_status || 'pending')}>
                  {policyData.policy_status || 'Pending'}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Policy Number</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">{policyNumber}</span>
              </div>
            </div>
          </Card>

          {/* Actions Card */}
          <Card>
            <CardHeader title="Actions" />
            <div className="space-y-3">
              {isEffective && policyData.pdf_url && (
                <a href={policyData.pdf_url} target="_blank" rel="noopener noreferrer" className="block">
                  <Button
                    variant="primary"
                    className="w-full justify-start"
                    leftIcon={<ArrowDownTrayIcon className="w-5 h-5" />}
                  >
                    Download Policy PDF
                  </Button>
                </a>
              )}
              {isEffective && !policyData.pdf_url && (
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  leftIcon={<ArrowDownTrayIcon className="w-5 h-5" />}
                  disabled
                >
                  PDF Generating...
                </Button>
              )}
              <Link to="/quotes" className="block">
                <Button variant="outline" className="w-full justify-start">
                  Back to Quotes
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
